import React from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import noop from '../../../../utils/noop';
import Heading from '../../heading';
import Paragraph from '../../paragraph';
import TextSkeleton from '../../skeletons/text-skeleton';
import ActionButton from '../../action-button';
import PlanPricing from '../plan-pricing';
import ButtonSkeleton from '../../skeletons/button-skeleton';
import { Popover, PopoverContent, PopoverTrigger } from '../../popover';
import Icon from '../../icon';

const defaultCaptions = {};

const Plan = ({
  captions = defaultCaptions,
  highlighted = false,
  disabled = false,
  isLoading = false,
  onPlanSelect = noop,
  isCompact = false,
  type,
  withExtraPrice = false,
}) => {
  const { name, description, cta, pricing, extraPrice, priceDescription } = captions;
  const classes = classNames('ws-ui-plan', {
    'ws-ui-plan--highlighted': highlighted,
    'ws-ui-plan--disabled': disabled,
    'ws-ui-plan--compact': isCompact,
  });
  const priceBtnClass = classNames('ws-ui-plan__price-button', {
    'ws-ui-plan__price-button--loading': isLoading,
  });

  const handlePlanSelect = () => {
    onPlanSelect(type);
  };

  return (
    <div className={classes}>
      <div className="ws-ui-plan__head">
        <Heading type="heading-3">
          {isLoading ? <TextSkeleton width="68px" centered /> : name}
        </Heading>
        {(isLoading || description) && (
          <div className="ws-ui-plan__description">
            <Paragraph appearance="medium-emphasis">
              {isLoading ? <TextSkeleton width="140px" centered /> : description}
            </Paragraph>
          </div>
        )}
      </div>
      <div className="ws-ui-plan__body">
        <PlanPricing {...pricing} withExtraPrice={withExtraPrice} isLoading={isLoading} />
        {withExtraPrice && !isLoading && (
          <div className="ws-ui-plan__extra-price">
            {extraPrice.infoText && (
              <Popover placement="bottom-center">
                <PopoverTrigger>
                  <Icon glyph="info" type="cta" size="16" />
                </PopoverTrigger>
                <PopoverContent>
                  <span
                    // eslint-disable-next-line yola/react/no-danger
                    dangerouslySetInnerHTML={{ __html: extraPrice.infoText }}
                  />
                </PopoverContent>
              </Popover>
            )}
            {extraPrice.price && (
              <div className="ws-ui-plan__extra-price-value">
                <Paragraph appearance="low-emphasis">{extraPrice.price}</Paragraph>
              </div>
            )}
          </div>
        )}
        {!isLoading && typeof priceDescription === 'string' && (
          <Paragraph appearance="medium-emphasis" size="small">
            {!isCompact && !priceDescription.trim() ? <>&nbsp;</> : priceDescription}
          </Paragraph>
        )}
        <div className={priceBtnClass}>
          {isLoading ? (
            <ButtonSkeleton />
          ) : (
            <ActionButton
              fullWidth={!isCompact}
              label={cta}
              appearance={highlighted ? 'accent' : 'default'}
              disabled={disabled}
              format={highlighted ? 'solid' : 'outline'}
              onClick={handlePlanSelect}
            />
          )}
        </div>
      </div>
    </div>
  );
};

Plan.propTypes = {
  captions: PropTypes.shape({
    pricing: PropTypes.shape({
      first: PropTypes.string,
      second: PropTypes.string,
      period: PropTypes.string,
    }),
    extraPrice: PropTypes.shape({
      infoText: PropTypes.string,
      price: PropTypes.string,
    }),
    priceDescription: PropTypes.string,
    name: PropTypes.string,
    description: PropTypes.string,
    cta: PropTypes.string,
  }),
  type: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  withExtraPrice: PropTypes.bool,
  isCompact: PropTypes.bool,
  highlighted: PropTypes.bool,
  disabled: PropTypes.bool,
  onPlanSelect: PropTypes.func,
};

export default Plan;
